import { motion } from "framer-motion";
import { Wind, Zap, DollarSign, LayoutDashboard, ArrowDown, CheckCircle2, BarChart3, Shield, Clock, Users } from "lucide-react";
import heroBg from "../assets/hero-bg.jpg";
import controlRoom from "../assets/control-room.jpg";
import Section from "../components/Section";
import SectionHeading from "../components/SectionHeading";
import PageHero from "../components/PageHero";
import EnergyFlowSVG from "../components/EnergyFlowSVG";
import EnergyParticles from "../components/EnergyParticles";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const steps = [
  {
    icon: Wind,
    title: "Project Development",
    desc: "Our engineering team identifies optimal locations using 3+ years of meteorological data, secures planning permits, negotiates grid interconnection agreements, and oversees construction of wind and solar infrastructure. Each project undergoes rigorous environmental impact assessment before breaking ground.",
    details: ["Site assessment & LiDAR studies", "Planning permits & environmental review", "Equipment procurement (Vestas, LONGi)", "Civil works & electrical installation", "Grid interconnection & commissioning"],
  },
  {
    icon: Zap,
    title: "Energy Generation",
    desc: "Once commissioned, our wind turbines and solar arrays begin generating clean electricity 24/7. All energy output is monitored in real-time from our London operations center. AI-powered systems optimize turbine pitch angles and solar tracker positions for maximum energy capture at every moment.",
    details: ["24/7 automated energy generation", "Real-time SCADA monitoring", "AI-powered output optimization", "Predictive maintenance systems", "97%+ availability rate"],
  },
  {
    icon: DollarSign,
    title: "Revenue Collection",
    desc: "Generated electricity is sold to national grid operators and utility companies through long-term Power Purchase Agreements (PPAs), typically spanning 15-25 years. Additional revenue streams include government renewable energy certificates (RECs), capacity payments, and ancillary grid services.",
    details: ["Long-term PPA contracts (15-25 years)", "Government renewable certificates", "Capacity & grid service payments", "Audited monthly revenue reports", "Multi-currency settlement"],
  },
  {
    icon: LayoutDashboard,
    title: "Partner Distribution",
    desc: "Revenue from energy sales is allocated to partners based on their participation level and the performance of the underlying assets. Distributions are calculated weekly, fully transparent, and accessible through our real-time partner dashboard. All figures are audited by independent third parties.",
    details: ["Weekly performance-based distributions", "Real-time dashboard access", "Independent audit verification", "Automated wallet crediting", "Full transaction history & statements"],
  },
];

const HowItWorks = () => (
  <div>
    <PageHero
      eyebrow="Our Process"
      title={<>A Transparent Path from <span className="energy-gradient-text">Energy to Revenue</span></>}
      description="Understand exactly how AeroVolt develops renewable energy projects and distributes performance-based revenue to global partners through a clear, four-step process."
      backgroundImage={heroBg}
    />

    {/* Steps */}
    <Section>
      <SectionHeading eyebrow="Four Steps" title="How Partner Participation Works" description="From project development to your weekly distribution — every step is transparent and verifiable." />
      <div className="max-w-4xl mx-auto space-y-6">
        {steps.map((step, i) => (
          <div key={i}>
            <motion.div
              {...fadeUp}
              transition={{ delay: i * 0.1 }}
              className="relative p-8 rounded-2xl glass-card glass-card-hover transition-all duration-500"
            >
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-shrink-0">
                  <div className="relative w-16 h-16 rounded-2xl energy-gradient flex items-center justify-center animate-glow-pulse">
                    <step.icon className="w-8 h-8 text-primary-foreground" />
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-background border-2 border-primary flex items-center justify-center font-display text-xs font-bold text-primary">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-xl font-bold mb-3">{step.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-5">{step.desc}</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {step.details.map((d, j) => (
                      <div key={j} className="flex items-center gap-2 text-xs">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                        <span className="text-foreground">{d}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
            {i < steps.length - 1 && (
              <div className="flex justify-center py-4">
                <motion.div {...fadeUp} transition={{ delay: i * 0.1 + 0.05 }}>
                  <ArrowDown className="w-5 h-5 text-primary/40 animate-pulse-glow" />
                </motion.div>
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>

    <div className="overflow-hidden"><EnergyFlowSVG className="h-16 opacity-30" /></div>

    {/* Dashboard Preview */}
    <Section className="bg-secondary/20 relative overflow-hidden">
      <EnergyParticles />
      <div className="relative z-10">
        <SectionHeading eyebrow="Your Dashboard" title="Monitor Everything in Real-Time" description="Every partner gets access to a comprehensive dashboard showing energy production, revenue, and portfolio performance." />
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <motion.div {...fadeUp}>
            <img src={controlRoom} alt="Dashboard preview" className="rounded-2xl w-full glow-box" />
          </motion.div>
          <motion.div {...fadeUp} className="space-y-4">
            {[
              { icon: BarChart3, title: "Energy Production Charts", desc: "Real-time energy generation data from every turbine and solar array in your portfolio." },
              { icon: DollarSign, title: "Revenue Tracking", desc: "See exactly how much revenue each asset generates and your proportional share." },
              { icon: Clock, title: "Distribution History", desc: "Complete history of all weekly distributions with downloadable statements." },
              { icon: Shield, title: "Audit Reports", desc: "Access independent audit reports verifying all revenue and distribution calculations." },
              { icon: Users, title: "Referral Network", desc: "Track your referral network growth, team volume, and bonus calculations." },
            ].map((f, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="flex gap-4 p-4 rounded-xl glass-card glass-card-hover transition-all duration-500 group">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                  <f.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-display text-sm font-semibold mb-1">{f.title}</h4>
                  <p className="text-muted-foreground text-xs leading-relaxed">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </Section>

    {/* Important Notes */}
    <Section>
      <div className="max-w-3xl mx-auto p-8 rounded-2xl border border-border bg-secondary/30">
        <h3 className="font-display text-lg font-bold mb-4 text-center">Important Information</h3>
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>⚠️ <strong className="text-foreground">No Guaranteed Returns:</strong> All distributions are performance-based and depend on actual energy production and revenue. Past performance does not guarantee future results.</p>
          <p>⚠️ <strong className="text-foreground">Market Risks:</strong> Energy prices fluctuate based on market conditions. Equipment can experience downtime due to weather or maintenance.</p>
          <p>⚠️ <strong className="text-foreground">Regulatory Compliance:</strong> AeroVolt operates within the regulatory framework of each jurisdiction. Financial promotions are subject to oversight by the Financial Conduct Authority (FCA) where applicable.</p>
        </div>
      </div>
    </Section>
  </div>
);

export default HowItWorks;
